import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTableModule } from '@angular/material/table';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ModuloService } from '../../../services/modulo/modulo.service';
import { SnackbarService } from '../../../services/snackbar/snackbar.service';

interface EmpresaItem {
  idEmpresa: number;
  nomeFantasia: string;
  razaoSocial?: string;
  cnpj: string;
  estado: string;
  cidade: string;
}

@Component({
  selector: 'app-adicionar-empresas-modulo-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatCheckboxModule,
    MatTableModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    MatSortModule,
  ],
  templateUrl: './adicionar-empresas-modulo-modal.html',
  styleUrl: './adicionar-empresas-modulo-modal.scss',
})
export class AdicionarEmpresasModuloModal implements OnInit {
  @Input() idModulo!: number;
  @Output() fechou = new EventEmitter<void>();
  @Output() empresasAdicionadas = new EventEmitter<void>();

  colunas = ['selecionar', 'nome', 'cnpj', 'estado', 'cidade'];

  @ViewChild(MatSort) sort!: MatSort;

  todasEmpresas: EmpresaItem[] = [];
  dataSource = new MatTableDataSource<EmpresaItem>();
  selecionadas: EmpresaItem[] = [];

  campoBusca: keyof EmpresaItem = 'nomeFantasia';
  termoBusca = '';
  isLoading = false;
  isSaving = false;

  get todasSelecionadas(): boolean {
    return (
      this.dataSource.data.length > 0 &&
      this.dataSource.data.every((e) => this.estaSelecionada(e))
    );
  }

  get algunsSelecionados(): boolean {
    return this.selecionadas.length > 0 && !this.todasSelecionadas;
  }

  constructor(
    private moduloService: ModuloService,
    private snackbarService: SnackbarService,
  ) {}

  async ngOnInit(): Promise<void> {
    await this.carregarEmpresas();
  }

  async carregarEmpresas(): Promise<void> {
    this.isLoading = true;
    try {
      // Busca apenas empresas não vinculadas a nenhum módulo
      const disponiveis = await this.moduloService.listarEmpresasDisponiveis(this.idModulo);

      this.todasEmpresas = disponiveis.map((e) => ({
        idEmpresa: e.idEmpresa,
        nomeFantasia: e.nomeFantasia,
        razaoSocial: e.nomeFantasia,
        cnpj: e.cnpj,
        estado: e.estado,
        cidade: e.cidade,
      }));

      this.dataSource.data = [...this.todasEmpresas];
      setTimeout(() => {
        this.dataSource.sort = this.sort;
      });
    } catch (err) {
      console.error('Erro ao carregar empresas:', err);
    } finally {
      this.isLoading = false;
    }
  }

  aplicarFiltro(): void {
    const termo = this.termoBusca.trim().toLowerCase();
    this.dataSource.data = this.todasEmpresas.filter((e) => {
      if (!termo) return true;
      const valor = String(e[this.campoBusca] || '').toLowerCase();
      return valor.includes(termo);
    });
  }

  estaSelecionada(empresa: EmpresaItem): boolean {
    return this.selecionadas.some((e) => e.idEmpresa === empresa.idEmpresa);
  }

  toggleEmpresa(empresa: EmpresaItem, checked: boolean): void {
    if (checked) {
      if (!this.estaSelecionada(empresa)) {
        this.selecionadas.push(empresa);
      }
    } else {
      this.selecionadas = this.selecionadas.filter(
        (e) => e.idEmpresa !== empresa.idEmpresa,
      );
    }
  }

  toggleTodas(checked: boolean): void {
    this.selecionadas = checked ? [...this.dataSource.data] : [];
  }

  limparSelecao(): void {
    this.selecionadas = [];
  }

  fechar(): void {
    this.fechou.emit();
  }

  async confirmar(): Promise<void> {
    if (this.selecionadas.length === 0) return;

    this.isSaving = true;
    try {
      const ids = this.selecionadas.map((e) => e.idEmpresa);
      await this.moduloService.adicionarEmpresas(this.idModulo, ids);
      this.snackbarService.success('Empresas adicionadas com sucesso!');
      this.empresasAdicionadas.emit();
      this.fechar();
    } catch (err) {
      console.error('Erro ao adicionar empresas:', err);
    } finally {
      this.isSaving = false;
    }
  }
}